import { type TypedDocumentNode, gql } from '@apollo/client';
import { faBell, faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useCallback } from 'react';
import styled from 'styled-components';

import { NotificationPreferenceChannel } from '__generated__/globalTypes';
import { IconButton } from 'atoms/buttons/IconButton';
import { IconDefinition } from 'atoms/icons';

import type { PreferenceToggleButton_notificationPreference } from './__generated__/index.graphql';

const Root = styled.div`
  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const typeToIcon: Record<Props['type'], IconDefinition> = {
  inAppPush: faBell,
  email: faEnvelope,
};

const typeToChannels: Record<Props['type'], NotificationPreferenceChannel[]> = {
  inAppPush: [
    NotificationPreferenceChannel.IN_APP,
    NotificationPreferenceChannel.PUSH,
  ],
  email: [NotificationPreferenceChannel.EMAIL],
};

type Props = {
  notificationPreference: PreferenceToggleButton_notificationPreference;
  type: 'inAppPush' | 'email';
  onChange: (channels: NotificationPreferenceChannel[]) => void;
};

export const PreferenceToggleButton = ({
  notificationPreference,
  type,
  onChange,
}: Props) => {
  const { channels, defaultChannels, availableChannels } =
    notificationPreference;

  const channelsToToggle = typeToChannels[type];

  const currentChannels = channels ?? defaultChannels;

  const enabled = channelsToToggle.some(c => currentChannels.includes(c));
  const editable = channelsToToggle.some(c => availableChannels.includes(c));

  const handleClick = useCallback(() => {
    const newChannels = enabled
      ? currentChannels?.filter(c => !channelsToToggle.includes(c))
      : [...currentChannels, ...channelsToToggle];

    // Only send available channels to the backend
    onChange(availableChannels.filter(c => newChannels.includes(c)));
  }, [availableChannels, channelsToToggle, currentChannels, enabled, onChange]);

  return (
    <Root className={classNames({ disabled: !editable })}>
      <IconButton
        size="small"
        color={enabled ? 'primary' : 'quaternary'}
        icon={typeToIcon[type]}
        onClick={editable ? handleClick : undefined}
      />
    </Root>
  );
};

PreferenceToggleButton.fragments = {
  notificationPreference: gql`
    fragment PreferenceToggleButton_notificationPreference on NotificationPreference {
      name
      channels
      defaultChannels
      availableChannels
    }
  ` as TypedDocumentNode<PreferenceToggleButton_notificationPreference>,
};
